import { type FC, useMemo } from "react";
import { cx, sva } from "@stubhub/broadway-foundation/css";
import { useFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { Image, ImageSize } from "src/website-apps/shared/components/image";
import {
  CLOUDINARY_TRANSFORMATION_PARAMS,
  COMMON_TRANSFORM_PARAMETERS,
  composeSrcWithCloudinaryTransformParams,
} from "src/website-apps/shared/components/image/utils/composeSrcWithCloudinaryTransformParams";
import {
  composeSrcWithVfsResizeParams,
  isVfsImageSrc,
} from "src/website-apps/shared/components/image/utils/composeSrcWithVfsResizeParams";

const userGeneratedImageRecipe = sva({
  slots: ["container", "image"],
  className: "userGeneratedImage",
  base: {
    container: {
      display: "block",
      width: "full",
    },
    image: {
      objectFit: "cover",
      borderStartStartRadius: "primitives.200",
      borderStartEndRadius: "primitives.200",
      borderEndStartRadius: "primitives.200",
      borderEndEndRadius: "primitives.200",
    },
  },
});

type UserGeneratedImageSlots = keyof ReturnType<typeof userGeneratedImageRecipe>;

const recipeClasses = userGeneratedImageRecipe();

interface Props {
  /** User image data */
  userImage: string;
  /** Category name for the user-generated image */
  categoryName: string;
  /** Additional CSS classes for slot customization */
  classes?: Partial<Record<UserGeneratedImageSlots, string>>;
}

/**
 * Component to display a user-generated image.
 * @returns A styled image card component
 */
export const UserGeneratedImage: FC<Props> = ({ userImage, categoryName, classes }) => {
  const storePageEvent = useStorePageEvent();
  const execVfsAsWebp = useFeatureTernary(FeatureSetEnum.TMPerformerVfsAsWebp);
  const optimizedSrc = useMemo(() => {
    const maxDevicePixelRatio = 2; // To support retina displays
    const width = ImageSize.UserGeneratedImage.width * maxDevicePixelRatio;
    const height = ImageSize.UserGeneratedImage.height * maxDevicePixelRatio;

    // VFS fan images ship without a size token and would otherwise load at full resolution.
    if (isVfsImageSrc(userImage)) {
      const useWebp = execVfsAsWebp({ control: () => false, variant: () => true, record: true }) ?? false;

      return composeSrcWithVfsResizeParams(userImage, width, height, { useWebp });
    }

    return composeSrcWithCloudinaryTransformParams(userImage, [
      ...COMMON_TRANSFORM_PARAMETERS,
      [[CLOUDINARY_TRANSFORMATION_PARAMS.CROP], "auto"],
      [[CLOUDINARY_TRANSFORMATION_PARAMS.HEIGHT], height],
      [[CLOUDINARY_TRANSFORMATION_PARAMS.WIDTH], width],
    ]);
  }, [userImage, execVfsAsWebp]);
  return (
    <div className={cx(recipeClasses.container, classes?.container)}>
      <Image
        className={cx(recipeClasses.image, classes?.image)}
        src={optimizedSrc}
        size={ImageSize.UserGeneratedImage}
        alt={categoryName}
        onClick={() => storePageEvent(PE_TYPE.CLICK, PE_KEYS.USER_GENERATED_IMAGE_CLICK, userImage)}
        onMouseEnter={() => storePageEvent(PE_TYPE.HOVER, PE_KEYS.USER_GENERATED_IMAGE_HOVER, userImage)}
      />
    </div>
  );
};
